import { Link, useNavigate } from 'react-router-dom';
import { useCatalogPath, useRouteLocale } from '@/hooks/useRouteLocale';
import { categoryHubPath } from '@/seo/categoryHubs';
import type { Category } from '@/types';
import { iconForCategory } from '@/utils/categoryIcons';

export type CategorySegment = 'brands' | 'shops' | 'promotions';

export interface CategoryChipsProps {
  categories: Category[];
  /** Override the search segment the chips navigate to. */
  segment?: CategorySegment;
  /** Smaller variant for dense contexts. */
  compact?: boolean;
}

/** Builds the un-prefixed /search URL for a category chip (the chip passes it through `useCatalogPath`). */
export function categorySearchUrl(category: Category, segment?: CategorySegment): string {
  return (
    `/search?categoryId=${category.id}` +
    `&categoryPath=${encodeURIComponent(category.path || '')}` +
    `&categoryTitle=${encodeURIComponent(category.title)}` +
    (segment ? `&segment=${segment}` : '')
  );
}

interface CategoryChipProps {
  category: Category;
  /** Kept for callers; the hub lists promotions and brands, so it no longer picks a search segment. */
  segment?: CategorySegment;
  compact?: boolean;
  className?: string;
}

/**
 * Single category chip: always a link to the category's hub
 * `/{cc}/{lang}/category/{slug-path}` (`categoryHubPath`: the backend's
 * `urlPath`, else the slug path in the route's locale; a level-3 category's is
 * its level-2 parent's hub). Only a category with no slug at all falls back to
 * the category search (a button), which is not a crawlable link.
 */
export function CategoryChip({ category, segment, compact, className }: CategoryChipProps) {
  const navigate = useNavigate();
  const locale = useRouteLocale();
  // `/pk/en/search?…` once the URL move is live (web plan W2.1).
  const catalog = useCatalogPath();
  const Icon = iconForCategory(category.title);
  const chipClass = `flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/15 font-semibold text-primary-strong transition-all duration-200 hover:bg-primary/25 ${
    compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'
  } ${className ?? ''}`;
  const content = (
    <>
      <Icon size={compact ? 12 : 14} aria-hidden="true" />
      <span className="whitespace-nowrap">{category.title}</span>
    </>
  );
  const hub = categoryHubPath(category, locale);
  if (hub) {
    return (
      <Link to={hub} className={chipClass}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={() => navigate(catalog(categorySearchUrl(category, segment)))} className={chipClass}>
      {content}
    </button>
  );
}

export function CategoryChips({ categories, segment, compact }: CategoryChipsProps) {
  if (categories.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {categories.map((category) => (
        <CategoryChip key={category.id} category={category} segment={segment} compact={compact} />
      ))}
    </div>
  );
}

export default CategoryChips;
